import { useNavigate } from 'react-router-dom';
import { useKDSStore, Station } from '../stores/kdsStore';
import { ArrowLeft, Volume2, Timer, LayoutGrid, Eye, LogOut } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import clsx from 'clsx';

const stations: Array<{ id: Station; label: string }> = [
  { id: 'all', label: 'All Stations' },
  { id: 'expo', label: 'Expo' },
  { id: 'grill', label: 'Grill' },
  { id: 'saute', label: 'Sauté' },
  { id: 'fry', label: 'Fry' },
  { id: 'salad', label: 'Salad' },
  { id: 'dessert', label: 'Dessert' },
  { id: 'bar', label: 'Bar' },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { settings, updateSettings } = useKDSStore();
  const { logout } = useAuthStore();

  return (
    <div className="h-[100dvh] flex flex-col bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-4 flex items-center gap-3 safe-top">
        <button
          onClick={() => navigate('/')}
          className="p-3 rounded-xl bg-slate-700 active:bg-slate-600 transition-colors btn-press"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-xl">Settings</h1>
      </header>

      {/* Settings Content */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="min-h-[calc(100%+1px)] p-4 sm:p-6 space-y-8 safe-bottom">
        {/* Default Station */}
        <section>
          <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold mb-4">
            <LayoutGrid className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500" />
            Default Station
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stations.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => updateSettings({ station: id })}
                className={clsx(
                  'px-4 py-4 rounded-xl font-semibold transition-all btn-press min-h-[56px]',
                  settings.station === id
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-800 text-slate-300 active:bg-slate-600'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        {/* Timing Thresholds */}
        <section>
          <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold mb-4">
            <Timer className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500" />
            Timing Thresholds
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm sm:text-base font-medium text-slate-400 mb-2">
                Warning Threshold (minutes)
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={settings.warningThresholdMinutes}
                onChange={(e) =>
                  updateSettings({
                    warningThresholdMinutes: parseInt(e.target.value) || 10,
                  })
                }
                className="w-full px-4 py-4 bg-slate-800 border-2 border-slate-700 rounded-xl text-white text-lg focus:outline-none focus:border-primary-500 min-h-[56px]"
              />
              <p className="text-sm text-slate-500 mt-2">
                Tickets will turn yellow after this time
              </p>
            </div>
            <div>
              <label className="block text-sm sm:text-base font-medium text-slate-400 mb-2">
                Critical Threshold (minutes)
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={settings.criticalThresholdMinutes}
                onChange={(e) =>
                  updateSettings({
                    criticalThresholdMinutes: parseInt(e.target.value) || 15,
                  })
                }
                className="w-full px-4 py-4 bg-slate-800 border-2 border-slate-700 rounded-xl text-white text-lg focus:outline-none focus:border-primary-500 min-h-[56px]"
              />
              <p className="text-sm text-slate-500 mt-2">
                Tickets will turn red and flash after this time
              </p>
            </div>
          </div>
        </section>

        {/* Display Settings */}
        <section>
          <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold mb-4">
            <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500" />
            Display Settings
          </h2>
          <div className="space-y-6">
            {/* Columns */}
            <div>
              <label className="block text-sm sm:text-base font-medium text-slate-400 mb-3">
                Number of Columns (Desktop)
              </label>
              <div className="flex gap-3">
                {[3, 4, 5, 6].map((cols) => (
                  <button
                    key={cols}
                    onClick={() => updateSettings({ columnsCount: cols })}
                    className={clsx(
                      'px-6 py-4 rounded-xl font-bold text-lg transition-all btn-press min-w-[56px] min-h-[56px]',
                      settings.columnsCount === cols
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-800 text-slate-300 active:bg-slate-600'
                    )}
                  >
                    {cols}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggles */}
            <div className="space-y-3">
              <label className="flex items-center justify-between p-4 sm:p-5 bg-slate-800 rounded-xl cursor-pointer active:bg-slate-700 transition-colors min-h-[72px]">
                <div className="flex-1 mr-4">
                  <span className="font-semibold text-base sm:text-lg">Compact Mode</span>
                  <p className="text-sm sm:text-base text-slate-500 mt-1">
                    Reduce spacing and show more tickets
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.compactMode}
                  onChange={(e) =>
                    updateSettings({ compactMode: e.target.checked })
                  }
                  className="w-6 h-6 accent-primary-600"
                />
              </label>

              <label className="flex items-center justify-between p-4 sm:p-5 bg-slate-800 rounded-xl cursor-pointer active:bg-slate-700 transition-colors min-h-[72px]">
                <div className="flex-1 mr-4">
                  <span className="font-semibold text-base sm:text-lg">Show Completed Items</span>
                  <p className="text-sm sm:text-base text-slate-500 mt-1">
                    Display completed items with strikethrough
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.showCompletedItems}
                  onChange={(e) =>
                    updateSettings({ showCompletedItems: e.target.checked })
                  }
                  className="w-6 h-6 accent-primary-600"
                />
              </label>

              <label className="flex items-center justify-between p-4 sm:p-5 bg-slate-800 rounded-xl cursor-pointer active:bg-slate-700 transition-colors min-h-[72px]">
                <div className="flex-1 mr-4">
                  <span className="font-semibold text-base sm:text-lg">Auto-Scroll</span>
                  <p className="text-sm sm:text-base text-slate-500 mt-1">
                    Automatically scroll to show oldest tickets
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.autoScrollEnabled}
                  onChange={(e) =>
                    updateSettings({ autoScrollEnabled: e.target.checked })
                  }
                  className="w-6 h-6 accent-primary-600"
                />
              </label>
            </div>
          </div>
        </section>

        {/* Sound Settings */}
        <section>
          <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold mb-4">
            <Volume2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500" />
            Sound Settings
          </h2>
          <label className="flex items-center justify-between p-4 sm:p-5 bg-slate-800 rounded-xl cursor-pointer active:bg-slate-700 transition-colors min-h-[72px]">
            <div className="flex-1 mr-4">
              <span className="font-semibold text-base sm:text-lg">Alert Sound</span>
              <p className="text-sm sm:text-base text-slate-500 mt-1">
                Play sound when new tickets arrive
              </p>
            </div>
            <input
              type="checkbox"
              checked={settings.alertSoundEnabled}
              onChange={(e) =>
                updateSettings({ alertSoundEnabled: e.target.checked })
              }
              className="w-6 h-6 accent-primary-600"
            />
          </label>

          {/* Instant answer to "is the sound working?" — a tap is a user
              gesture, so audio is always allowed here */}
          <button
            onClick={() => {
              try {
                const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
                if (ctx.state === 'suspended') ctx.resume();
                const tone = (f: number, t0: number, d: number) => {
                  const o = ctx.createOscillator();
                  const g = ctx.createGain();
                  o.connect(g); g.connect(ctx.destination);
                  o.frequency.value = f; o.type = 'sine';
                  g.gain.setValueAtTime(0, t0);
                  g.gain.linearRampToValueAtTime(0.3, t0 + 0.02);
                  g.gain.linearRampToValueAtTime(0, t0 + d);
                  o.start(t0); o.stop(t0 + d);
                };
                const now = ctx.currentTime;
                tone(880, now, 0.15);
                tone(1108.73, now + 0.15, 0.25);
              } catch { /* nothing to do */ }
            }}
            className="w-full mt-3 py-3 rounded-xl bg-slate-800 text-primary-400 font-semibold active:bg-slate-700 transition-colors flex items-center justify-center gap-2"
          >
            <Volume2 className="w-5 h-5" />
            Play test sound
          </button>
        </section>

        {/* Sign out */}
        <section className="pt-2">
          <button
            onClick={() => logout()}
            className="w-full py-3 rounded-xl bg-slate-800 text-slate-300 font-semibold active:bg-slate-700 transition-colors flex items-center justify-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            Sign out of this kitchen display
          </button>
          <p className="mt-2 text-center text-xs text-slate-500">
            You'll need the restaurant code to sign back in.
          </p>
        </section>
      </div>
      </div>
    </div>
  );
}
